import { useQueryClient } from "@suse-o11y/query";
import { ReactQueryDevtools } from "@suse-o11y/query/devtools";
import React, { useEffect, useState } from "react";

import { bootLoad } from "./app/BootLoaderService";
import { LoadingScreen } from "./app/LoadingScreen";
import { LoadingStackStateErrorScreen } from "./app/LoadingStackstateErrorScreen";
import { Devtools } from "./components/devtools";
import { isAboutToExpire } from "./components/react/license/is-about-to-expire";
import { LicenseWarning } from "./components/react/license/license-warning";
import { TrialExpired } from "./components/react/license/trial/TrialExpired";
import { ProgressBar } from "./components/react/progress-bar/ProgressBar";
import { RelativeContainer } from "./components/react/RelativeContainer";
import { isUnauthorizedError } from "./generic/request";
import { useQuery } from "./queries/react-query";
import { StsRouter } from "./routes/StsRouter";
import { Providers, type ProvidersProps } from "./Providers";
import { queryLicenseConfig } from "./queries";
export type RootProps = Pick<ProvidersProps, "store" | "history">;

export const Root: React.FC<RootProps> = ({ store, history }) => {
	const [loading, setLoading] = useState(true);
	const [unauthorized, setUnauthorized] = useState(false);
	const [bootLoaderFinished, setBootLoaderFinished] = useState(false);
	const [showLicenseWarning, setShowLicenseWarning] = React.useState(true);
	const queryClient = useQueryClient();

	const { isLoading: isLicenseLoading, data: license } = useQuery({
		...queryLicenseConfig,
		enabled: bootLoaderFinished,
	});

	// Show the warning again after the license info is refetched, even if the user previously dismissed it
	useEffect(() => {
		if (license != null) {
			const isLicensedProblem = license.isTrial
				? license.error === "expired"
				: license.error != null || isAboutToExpire(license.daysLeftToExpire);

			setShowLicenseWarning(isLicensedProblem);
		}
	}, [license]);

	useEffect(() => {
		bootLoad(store, queryClient)
			.then(() => {
				setLoading(false);
				return setBootLoaderFinished(true);
			})
			.catch((error) => {
				if (isUnauthorizedError(error)) {
					// do not display error screen as it will keep as bootLoaderFinished nto done yet,
					// it should redirect to the login page eventually while showing the progress bar and loading screen
					// but preventing the router to be instantiated as the bootLoaderFinished is not done
					setUnauthorized(true);
				} else {
					setLoading(false);
					// it will display LoadingStackStateErrorScreen
				}
			});
	}, [store, queryClient]);

	if (loading || (bootLoaderFinished && isLicenseLoading) || unauthorized) {
		return (
			<>
				<ProgressBar />
				<LoadingScreen />
			</>
		);
	}

	if (!bootLoaderFinished) {
		return <LoadingStackStateErrorScreen />;
	}

	if (showLicenseWarning && license != null) {
		if (license.isTrial) {
			return <TrialExpired />;
		}
		return (
			<LicenseWarning
				daysLeft={license.daysLeftToExpire}
				error={license.error}
				onDismiss={() => setShowLicenseWarning(false)}
			/>
		);
	}

	return (
		<RelativeContainer className="h-full min-h-full min-w-app-min transition duration-300 ease-linear has-active-horizontal-resizable:**:cursor-ew-resize has-active-vertical-resizable:**:cursor-ns-resize">
			<Providers history={history} store={store}>
				<StsRouter />
				<ReactQueryDevtools />
				{import.meta.env.DEV && <Devtools />}
			</Providers>
		</RelativeContainer>
	);
};
